import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Boxes } from 'lucide-react';

import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';
import BasicPagination from '../../../components/DataTable/BasicPagination';
import SectionWrapper from '../../../components/SectionWrapper/SectionWrapper';
import useRestaurantRawMaterials from '../../../hooks/restaurant/useRestaurantRawMaterials';
import useDebouncedValue from '../../../hooks/useDebouncedValue';
import WorkspaceLayout from '../../../layout/WorkspaceLayout';
import BranchService from '../../../services/branch/BranchService';
import { useAuthUser } from '../../../hooks/auth/useAuthUser';
import { IBranchDropdown } from '../../../types/branches/branchInterfaces';
import {
  RestaurantRawMaterialDTO,
  RestaurantRawMaterialFilters,
} from '../../../types/restaurant/RestaurantRawMaterialTypes';
import { RestaurantPageRequest } from '../../../types/restaurant/RestaurantRecipeTypes';

import RawMaterialFormModal from './RawMaterialFormModal';
import RawMaterialIdentifiersModal from './RawMaterialIdentifiersModal';
import RawMaterialCard from './components/RawMaterialCard';
import RawMaterialTable from './components/RawMaterialTable';
import RawMaterialFilters, {
  RawMaterialActiveFilter,
} from './components/RawMaterialFilters';
import RawMaterialHeader from './components/RawMaterialHeader';

const RestaurantRawMaterialsScreen = () => {
  // =====================================================
  // 🔹 Sucursales
  // =====================================================

  const { hasRole } = useAuthUser();
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const [branches, setBranches] = useState<IBranchDropdown[]>([]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [loadingBranches, setLoadingBranches] = useState(true);

  // =====================================================
  // 🔹 Filtros
  // =====================================================

  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState(0);
  const [unitId, setUnitId] = useState(0);
  const [supplierId, setSupplierId] = useState(0);

  const [activeFilter, setActiveFilter] =
    useState<RawMaterialActiveFilter>('ACTIVE');

  // =====================================================
  // 🔹 Paginación
  // =====================================================

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [sort] = useState(['name,asc', 'id,asc']);

  // =====================================================
  // 🔹 Modales
  // =====================================================

  const [identifierMaterial, setIdentifierMaterial] =
    useState<RestaurantRawMaterialDTO | null>(null);

  // =====================================================
  // 🔹 Hooks
  // =====================================================

  const debouncedSearch = useDebouncedValue(search, 350);
  const materials = useRestaurantRawMaterials(branchId);

  // =====================================================
  // 🔹 Cargar sucursales
  // =====================================================

  useEffect(() => {
    const request = canPickBranch
      ? BranchService.fetchDropdown()
      : BranchService.fetchMyBranch().then((mine) => [mine]);

    request
      .then((items) => {
        setBranches(items);

        if (items.length > 0) {
          setBranchId(items[0].id);
        }
      })
      .finally(() => setLoadingBranches(false));
  }, [canPickBranch]);

  // =====================================================
  // 🔹 Cargar opciones de materia prima
  // =====================================================

  useEffect(() => {
    if (branchId) {
      materials.loadOptions();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  // =====================================================
  // 🔹 Construcción de petición
  // =====================================================

  const request = useMemo<RestaurantPageRequest<RestaurantRawMaterialFilters>>(
    () => ({
      page,
      size,
      sort,
      filters: {
        search: debouncedSearch.trim() || null,
        categoryId: categoryId || null,
        unitId: unitId || null,
        supplierId: supplierId || null,
        active: activeFilter === 'ALL' ? null : activeFilter === 'ACTIVE',
      },
    }),
    [
      activeFilter,
      categoryId,
      debouncedSearch,
      page,
      size,
      sort,
      supplierId,
      unitId,
    ]
  );

  // =====================================================
  // 🔹 Cargar listado
  // =====================================================

  useEffect(() => {
    if (branchId) {
      materials.load(request);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, request]);

  // =====================================================
  // 🔹 Navegación de página
  // =====================================================

  const previousPage = () => {
    setPage((current) => Math.max(0, current - 1));
  };

  // =====================================================
  // 🔹 Reiniciar filtros
  // =====================================================

  const resetFilters = () => {
    setSearch('');
    setCategoryId(0);
    setUnitId(0);
    setSupplierId(0);
    setActiveFilter('ACTIVE');
    setPage(0);
  };

  // =====================================================
  // 🔹 Eventos del header
  // =====================================================

  const handleBranchChange = (nextBranchId: number) => {
    resetFilters();
    setBranchId(nextBranchId);
  };

  // =====================================================
  // 🔹 Eventos de filtros
  // =====================================================

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const handleCategoryChange = (value: number) => {
    setCategoryId(value);
    setPage(0);
  };

  const handleUnitChange = (value: number) => {
    setUnitId(value);
    setPage(0);
  };

  const handleSupplierChange = (value: number) => {
    setSupplierId(value);
    setPage(0);
  };

  const handleActiveChange = (value: RawMaterialActiveFilter) => {
    setActiveFilter(value);
    setPage(0);
  };

  // =====================================================
  // 🔹 Eventos de tarjeta
  // =====================================================

  const handleToggleActive = (material: RestaurantRawMaterialDTO) => {
    materials.toggleActive(material, request, previousPage);
  };

  // =====================================================
  // 🔹 Estadísticas de la página actual
  // =====================================================

  const activeCount = materials.list.filter((item) => item.active).length;

  const lowStockCount = materials.list.filter(
    (item) => item.active && item.lowStock
  ).length;

  const usedCategoriesCount = new Set(
    materials.list.filter((item) => item.active).map((item) => item.categoryId)
  ).size;

  // =====================================================
  // 🔹 Validaciones
  // =====================================================

  const createDisabled =
    !branchId ||
    materials.categories.length === 0 ||
    materials.units.length === 0;

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />

      <SectionWrapper>
        {/* =====================================================
            🔹 Header
        ===================================================== */}

        <RawMaterialHeader
          branches={branches}
          branchId={branchId}
          loadingBranches={loadingBranches}
          canPickBranch={canPickBranch}
          createDisabled={createDisabled}
          onBranchChange={handleBranchChange}
          onCreate={materials.openCreate}
        />

        {/* =====================================================
            🔹 Resumen
        ===================================================== */}

        <div
          className="
            grid
            grid-cols-1
            gap-3

            border-b
            border-stroke

            bg-gray-2/40

            p-5

            dark:border-strokedark
            dark:bg-boxdark-2/30

            sm:grid-cols-3
            sm:px-7
          "
        >
          <div
            className="
              rounded-xl

              border
              border-stroke

              bg-white

              p-4

              shadow-card-2

              dark:border-strokedark
              dark:bg-boxdark
              dark:shadow-none
            "
          >
            <p
              className="
                text-xs
                font-medium
                uppercase
                tracking-wide
                text-bodydark2
              "
            >
              Activas
            </p>

            <p
              className="
                mt-1
                text-2xl
                font-semibold
                text-black

                dark:text-white
              "
            >
              {activeCount}
            </p>
          </div>

          <div
            className="
              rounded-xl

              border
              border-stroke

              bg-white

              p-4

              shadow-card-2

              dark:border-strokedark
              dark:bg-boxdark
              dark:shadow-none
            "
          >
            <p
              className="
                text-xs
                font-medium
                uppercase
                tracking-wide
                text-bodydark2
              "
            >
              Stock bajo en página
            </p>

            <p
              className="
                mt-1

                flex
                items-center
                gap-2

                text-2xl
                font-semibold
                text-meta-8

                dark:text-warning
              "
            >
              <AlertTriangle className="h-5 w-5" />

              {lowStockCount}
            </p>
          </div>

          <div
            className="
              rounded-xl

              border
              border-stroke

              bg-white

              p-4

              shadow-card-2

              dark:border-strokedark
              dark:bg-boxdark
              dark:shadow-none
            "
          >
            <p
              className="
                text-xs
                font-medium
                uppercase
                tracking-wide
                text-bodydark2
              "
            >
              Categorías utilizadas
            </p>

            <p
              className="
                mt-1
                text-2xl
                font-semibold
                text-black

                dark:text-white
              "
            >
              {usedCategoriesCount}
            </p>
          </div>
        </div>

        {/* =====================================================
            🔹 Filtros
        ===================================================== */}

        <RawMaterialFilters
          search={search}
          categoryId={categoryId}
          unitId={unitId}
          supplierId={supplierId}
          activeFilter={activeFilter}
          categories={materials.categories}
          units={materials.units}
          suppliers={materials.suppliers}
          onSearchChange={handleSearchChange}
          onCategoryChange={handleCategoryChange}
          onUnitChange={handleUnitChange}
          onSupplierChange={handleSupplierChange}
          onActiveChange={handleActiveChange}
        />

        {/* =====================================================
            🔹 Cargando
        ===================================================== */}

        {materials.loading ? (
          <div
            className="
              grid
              grid-cols-1
              gap-4

              p-5

              sm:px-7
              md:grid-cols-2
              xl:grid-cols-3
            "
          >
            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className="
                  h-72

                  animate-pulse

                  rounded-xl

                  border
                  border-stroke

                  bg-gray-2

                  dark:border-strokedark
                  dark:bg-meta-4
                "
              />
            ))}
          </div>
        ) : materials.list.length === 0 ? (
          /* =====================================================
              🔹 Estado vacío
          ===================================================== */

          <div
            className="
              flex
              min-h-[320px]
              flex-col
              items-center
              justify-center

              px-5

              text-center

              sm:px-7
            "
          >
            <span
              className="
                rounded-xl

                border
                border-primary/15

                bg-primary/10

                p-3

                text-primary

                dark:border-primary/25
                dark:bg-primary/15
              "
            >
              <Boxes className="h-8 w-8" />
            </span>

            <h3
              className="
                mt-4
                font-semibold
                text-black

                dark:text-white
              "
            >
              No hay materias primas para mostrar
            </h3>

            <p
              className="
                mt-1
                max-w-md
                text-sm
                text-bodydark2
              "
            >
              Cambie los filtros seleccionados o registre una nueva materia
              prima para esta sucursal.
            </p>
          </div>
        ) : (
          /* =====================================================
              🔹 Listado (tabla en escritorio, tarjetas en móvil)
          ===================================================== */

          <>
            <RawMaterialTable
              materials={materials.list}
              onIdentifiers={setIdentifierMaterial}
              onEdit={materials.edit}
              onToggleActive={handleToggleActive}
            />

            <div className="grid grid-cols-1 gap-4 p-5 sm:px-7 md:hidden">
              {materials.list.map((material) => (
                <RawMaterialCard
                  key={material.id}
                  material={material}
                  onIdentifiers={setIdentifierMaterial}
                  onEdit={materials.edit}
                  onToggleActive={handleToggleActive}
                />
              ))}
            </div>
          </>
        )}

        {/* =====================================================
            🔹 Paginación
        ===================================================== */}

        <div
          className="
            border-t
            border-stroke

            px-5
            pb-5
            pt-4

            dark:border-strokedark

            sm:px-7
          "
        >
          <BasicPagination
            currentPage={materials.page?.page.number ?? 0}
            totalPages={materials.page?.page.totalPages ?? 0}
            totalElements={materials.page?.page.totalElements ?? 0}
            size={materials.page?.page.size ?? size}
            first={materials.page ? materials.page.page.number === 0 : true}
            last={materials.page ? materials.page.page.number >= materials.page.page.totalPages - 1 : true}
            onPageChange={setPage}
            onSizeChange={(value) => {
              setSize(value);
              setPage(0);
            }}
          />
        </div>
      </SectionWrapper>

      {/* =====================================================
          🔹 Modal crear/editar
      ===================================================== */}

      <RawMaterialFormModal
        isOpen={materials.modalOpen}
        form={materials.form}
        categories={materials.categories}
        units={materials.units}
        suppliers={materials.suppliers}
        saving={materials.saving}
        onClose={materials.closeModal}
        onChange={materials.updateField}
        onSave={() => materials.save(request, previousPage)}
      />

      {/* =====================================================
          🔹 Modal de identificadores
      ===================================================== */}

      <RawMaterialIdentifiersModal
        isOpen={Boolean(identifierMaterial)}
        branchId={Number(branchId || 0)}
        rawMaterial={identifierMaterial}
        suppliers={materials.suppliers}
        onClose={() => setIdentifierMaterial(null)}
      />
    </WorkspaceLayout>
  );
};

export default RestaurantRawMaterialsScreen;
